import React, { useCallback, memo } from 'react';
import { Info } from 'lucide-react';
import { FormInput, FormGroup, FormToggle, HelpText, WarningBox } from '../../common/form';

import styles from '../styles/AgentSettings.module.css';

// Character limits for text fields
const CUSTOM_SAFETY_PROMPT_MAX = 2000;

const AgentSafetyTab = ({ data, onChange }) => {
    const handleToggle = useCallback((field, value) => {
        onChange({ [field]: value });
    }, [onChange]);

    const handleChange = useCallback((field, value) => {
        onChange({ [field]: value });
    }, [onChange]);

    return (
        <div className={styles.tabContent}>
            <div className={styles.section}>
                <h3 className={styles.sectionTitle}>Safety Settings</h3>
                <p className={styles.sectionDescription}>
                    When enabled, we additionally check all agent responses against your custom guidelines. These guidelines are also added to the main agent prompt and may be included in tool calls.
                </p>

                <WarningBox>
                    <p>
                        Enabling safety checks adds an additional processing step to every message, 
                        which may increase response time and token usage.
                    </p>
                </WarningBox>

                <FormGroup>
                    <FormToggle
                        checked={data.enableSafetyChecks}
                        onChange={value => handleToggle('enableSafetyChecks', value)}
                        title="Enable Custom Safety Prompt"
                        description="Use custom safety guidelines for your agent's responses"
                    />
                </FormGroup>
            </div>

            {data?.enableSafetyChecks && (
                <div className={styles.section}>
                    <h3 className={styles.sectionTitle}>Custom Safety Prompt</h3>
                    <p className={styles.sectionDescription}>
                        Define custom safety guidelines and content restrictions for your agent. Use this prompt to specify what topics to avoid or allow, what data to protect, and any specific behavioral guidelines.
                    </p>

                    <FormGroup>
                        <FormInput
                            type="textarea"
                            id="customSafetyPrompt"
                            value={data.customSafetyPrompt}
                            onChange={value => handleChange('customSafetyPrompt', value)}
                            rows={6}
                            placeholder="Enter your custom safety guidelines here..."
                            maxLength={CUSTOM_SAFETY_PROMPT_MAX}
                        />
                        <div className={styles.helpRow}>
                            <HelpText>
                                If the field is left empty, then no additional safety checks will be performed on agent responses
                            </HelpText>
                            <HelpText className={styles.helpTextRight}>
                                {(data.customSafetyPrompt || '').length}/{CUSTOM_SAFETY_PROMPT_MAX}
                            </HelpText>
                        </div>
                    </FormGroup>

                    <div className={styles.infoBox}>
                        <Info size={16} />
                        <div>
                            <p>Examples of what you can specify:</p>
                            <ul>
                                <li><strong>Topic restrictions:</strong> "Do not discuss political opinions or provide medical advice"</li>
                                <li><strong>Allowed topics:</strong> "Only discuss topics related to project management and team coordination"</li>
                                <li><strong>Data protection:</strong> "For calendar data, only share available time slots but never event names or details"</li>
                                <li><strong>Behavioral guidelines:</strong> "Always maintain a professional tone and avoid casual language"</li>
                            </ul>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default memo(AgentSafetyTab);
